import {cn} from '@tempmee/core'
import {type HTMLAttributes, type ReactNode, type Ref, forwardRef} from 'react'

export type DividerOrientation = 'horizontal' | 'vertical'
export type DividerVariant = 'fullWidth' | 'inset' | 'middle'
export type DividerTextAlign = 'center' | 'left' | 'right'

export interface DividerProps extends HTMLAttributes<HTMLElement> {
  /** Line direction. @default 'horizontal' */
  orientation?: DividerOrientation
  /** Indentation: `fullWidth` (edge-to-edge), `inset` (left-indented), `middle` (both margins). @default 'fullWidth' */
  variant?: DividerVariant
  /** For a vertical divider inside a flex container, stretch to the container's height. */
  flexItem?: boolean
  /** Position of `children` along the line. @default 'center' */
  textAlign?: DividerTextAlign
  /** Optional content rendered inline, splitting the line (e.g. an "OR" label). */
  children?: ReactNode
}

const LINE = 'border-divider'

/** Horizontal indentation per variant (only applies to horizontal dividers). */
const H_VARIANT: Record<DividerVariant, string> = {
  fullWidth: '',
  inset: 'ml-[72px]',
  middle: 'mx-4',
}

/**
 * Divider (v3, MUI-aligned) — a thin rule separating content.
 *
 * Mirrors `@mui/material/Divider`: `orientation`, `variant`
 * (`fullWidth`/`inset`/`middle`), `flexItem`, `textAlign`, and inline `children`.
 * Renders a semantic `<hr>` when empty; a `<div role="separator">` when it has children.
 */
export const Divider = forwardRef<HTMLElement, DividerProps>(
  (
    {
      orientation = 'horizontal',
      variant = 'fullWidth',
      flexItem = false,
      textAlign = 'center',
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const isVertical = orientation === 'vertical'

    // Plain rule (no label) → semantic <hr>.
    if (children == null) {
      return (
        <hr
          ref={ref as Ref<HTMLHRElement>}
          aria-orientation={isVertical ? 'vertical' : 'horizontal'}
          className={cn(
            'm-0 shrink-0 border-0',
            isVertical
              ? cn('h-full self-stretch border-l', LINE, flexItem && 'h-auto')
              : cn('w-full border-t', LINE, H_VARIANT[variant]),
            className,
          )}
          {...props}
        />
      )
    }

    // Labelled rule → role="separator" with a line on each side of the content.
    const leftGrow = textAlign === 'left' ? 'w-[10%] grow-0' : 'flex-1'
    const rightGrow = textAlign === 'right' ? 'w-[10%] grow-0' : 'flex-1'

    return (
      // A labelled divider is a static (non-focusable) separator wrapping its
      // label — matches MUI's markup. `<hr>` is void so it can't hold children.
      // biome-ignore lint/a11y/useFocusableInteractive: static separator, not a resize splitter
      <div
        ref={ref as Ref<HTMLDivElement>}
        // biome-ignore lint/a11y/useSemanticElements: <hr> cannot contain the label children
        role="separator"
        aria-orientation={isVertical ? 'vertical' : 'horizontal'}
        className={cn(
          'flex items-center text-text-secondary text-sm',
          isVertical ? 'h-full flex-col' : 'w-full',
          className,
        )}
        {...props}
      >
        <span className={cn('border-divider', isVertical ? 'border-l' : 'border-t', leftGrow)} />
        <span className={cn('shrink-0', isVertical ? 'py-1' : 'px-2')}>{children}</span>
        <span className={cn('border-divider', isVertical ? 'border-l' : 'border-t', rightGrow)} />
      </div>
    )
  },
)
Divider.displayName = 'Divider'
